// ============================================================================
// OBT F3 — İnceleme / karar API katmanı (paket-yerel)
// ============================================================================
// Ortak `obtApi.js` BÜYÜTÜLMEZ; F3 uçları burada toplanır. Tüm yollar backend
// `modules/obt/api.py` ile doğrulanmıştır (dual-mount `/obt` → /api + /api/v1).
// Aktör kimliği HİÇBİR çağrıda body/query ile GÖNDERİLMEZ; backend JWT'den okur.
//
// Doğrulanmış sözleşme (özet):
//   GET  /obt/review/{bm|my|director}-queue          → ReviewQueueItem[]
//   GET  /obt/responses/{id}/review                  → ReviewDetailOut
//   POST /obt/responses/{id}/bm/{start|approve|reject|request-revision|note}
//   POST /obt/responses/{id}/my/{start|approve|request-revision|send-to-director}
//   GET  /obt/publications/{id}/suggested-parks      → ReviewerSuggestedParkOut[]
//   POST /obt/suggested-parks/{id}/{approve|reject}
//   GET  /obt/suggested-parks/{id}/media/{photo_id}  → blob (ACL)
//   GET  /obt/responses/{id}/media/{photo_id}        → blob (ACL)
//   POST /obt/decision-sets  PUT .../items  POST .../items/remove  .../finalize
//   POST /obt/decision-sets/revise  GET /obt/decision-sets/{id}
//   GET  /obt/campaigns/{id}/decision-sets           → DecisionSetHistoryOut
//   GET  /obt/campaigns  GET /obt/campaigns/{id}
//   GET  /obt/campaigns/{id}/{pending|late}          → RespondentStatusPage
//   GET  /organizasyon/chains/child/{id}             → {chain[]}
// ============================================================================

import apiClient from '@/api/client';

const OBT = '/obt';
const ORG = '/organizasyon';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// İnceleme kuyrukları (kapsam backend'de donmuş-zincir ile çözülür)
// ---------------------------------------------------------------------------

function queueParams({ campaignId, publicationId, topic } = {}) {
  const params = {};
  if (campaignId) params.campaign_id = campaignId;
  if (publicationId) params.publication_id = publicationId;
  if (topic) params.topic = topic;
  return params;
}

export async function listBmQueue(filters = {}, { signal } = {}) {
  const res = await apiClient.get(`${OBT}/review/bm-queue`, {
    params: queueParams(filters),
    signal,
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function listMyQueue(filters = {}, { signal } = {}) {
  const res = await apiClient.get(`${OBT}/review/my-queue`, {
    params: queueParams(filters),
    signal,
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function listDirectorQueue(filters = {}, { signal } = {}) {
  const res = await apiClient.get(`${OBT}/review/director-queue`, {
    params: queueParams(filters),
    signal,
  });
  return Array.isArray(res.data) ? res.data : [];
}

// ---------------------------------------------------------------------------
// İnceleme dosyası
// ---------------------------------------------------------------------------

export async function getReviewDetail(responseId, { signal } = {}) {
  const res = await apiClient.get(`${OBT}/responses/${enc(responseId)}/review`, {
    signal,
  });
  return res.data; // ReviewDetailOut
}

// ---------------------------------------------------------------------------
// BM inceleme işlemleri (→ ReviewTransitionResult)
// ---------------------------------------------------------------------------

export async function bmStart(responseId, { signal } = {}) {
  const res = await apiClient.post(`${OBT}/responses/${enc(responseId)}/bm/start`, {}, { signal });
  return res.data;
}
export async function bmApprove(responseId, { comment } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/responses/${enc(responseId)}/bm/approve`,
    { comment: comment ?? null },
    { signal }
  );
  return res.data;
}
export async function bmReject(responseId, { comment }, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/responses/${enc(responseId)}/bm/reject`,
    { comment },
    { signal }
  );
  return res.data;
}
export async function bmRequestRevision(responseId, { comment }, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/responses/${enc(responseId)}/bm/request-revision`,
    { comment },
    { signal }
  );
  return res.data;
}
export async function bmNote(responseId, { note }, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/responses/${enc(responseId)}/bm/note`,
    { note },
    { signal }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// MY inceleme işlemleri
// ---------------------------------------------------------------------------

export async function myStart(responseId, { signal } = {}) {
  const res = await apiClient.post(`${OBT}/responses/${enc(responseId)}/my/start`, {}, { signal });
  return res.data;
}
export async function myApprove(responseId, { comment } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/responses/${enc(responseId)}/my/approve`,
    { comment: comment ?? null },
    { signal }
  );
  return res.data;
}
export async function myRequestRevision(responseId, { comment }, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/responses/${enc(responseId)}/my/request-revision`,
    { comment },
    { signal }
  );
  return res.data;
}
export async function mySendToDirector(responseId, { comment } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/responses/${enc(responseId)}/my/send-to-director`,
    { comment: comment ?? null },
    { signal }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Filtre-dışı park önerileri (F3 enabler)
// ---------------------------------------------------------------------------

export async function listSuggestedParks(publicationId, { status } = {}, { signal } = {}) {
  const params = {};
  if (status) params.status = status;
  const res = await apiClient.get(
    `${OBT}/publications/${enc(publicationId)}/suggested-parks`,
    { params, signal }
  );
  return Array.isArray(res.data) ? res.data : [];
}
export async function approveSuggestedPark(suggestionId, { comment } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/suggested-parks/${enc(suggestionId)}/approve`,
    { comment: comment ?? null },
    { signal }
  );
  return res.data;
}
export async function rejectSuggestedPark(suggestionId, { comment }, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/suggested-parks/${enc(suggestionId)}/reject`,
    { comment },
    { signal }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// ACL'li medya okuma (blob). Object URL çağıran tarafından revoke edilmelidir.
// ---------------------------------------------------------------------------

async function readBlob(url, signal) {
  const res = await apiClient.get(url, { signal, responseType: 'blob' });
  const blob = res.data;
  return {
    objectUrl: URL.createObjectURL(blob),
    mimeType: blob?.type || res.headers?.['content-type'] || 'image/*',
  };
}

export function readResponsePhoto(responseId, photoId, { signal } = {}) {
  return readBlob(`${OBT}/responses/${enc(responseId)}/media/${enc(photoId)}`, signal);
}
export function readSuggestionPhoto(suggestionId, photoId, { signal } = {}) {
  return readBlob(`${OBT}/suggested-parks/${enc(suggestionId)}/media/${enc(photoId)}`, signal);
}

// ---------------------------------------------------------------------------
// Müdür nihai karar seti
// ---------------------------------------------------------------------------

export async function createDecisionSet({ campaignId, note } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/decision-sets`,
    { campaign_id: campaignId, note: note ?? null },
    { signal }
  );
  return res.data; // DecisionSetResult
}
export async function upsertDecisionItem(
  decisionSetId,
  { responseId, decision, explanation } = {},
  { signal } = {}
) {
  const res = await apiClient.put(
    `${OBT}/decision-sets/${enc(decisionSetId)}/items`,
    { response_id: responseId, decision, explanation: explanation ?? null },
    { signal }
  );
  return res.data;
}
export async function removeDecisionItem(decisionSetId, { responseId } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/decision-sets/${enc(decisionSetId)}/items/remove`,
    { response_id: responseId },
    { signal }
  );
  return res.data;
}
export async function finalizeDecisionSet(decisionSetId, { decisionText } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/decision-sets/${enc(decisionSetId)}/finalize`,
    { decision_text: decisionText ?? null },
    { signal }
  );
  return res.data;
}
export async function reviseDecisionSet(
  { campaignId, note, copyPreviousItems = true } = {},
  { signal } = {}
) {
  const res = await apiClient.post(
    `${OBT}/decision-sets/revise`,
    { campaign_id: campaignId, note: note ?? null, copy_previous_items: copyPreviousItems },
    { signal }
  );
  return res.data;
}
export async function getDecisionSet(decisionSetId, { signal } = {}) {
  const res = await apiClient.get(`${OBT}/decision-sets/${enc(decisionSetId)}`, { signal });
  return res.data;
}

// --- Müdür nihai listeye SONRADAN park ekleme (F3 tamamlama) -----------------
// Aday kümesi DIŞINDAN park: cevap/inceleme YOK; kaynak `director_added`.
export async function listDirectorParkCandidates(
  decisionSetId,
  { search, regionId, limit } = {},
  { signal } = {}
) {
  const params = {};
  if (search) params.search = search;
  if (regionId) params.region_id = regionId;
  if (typeof limit === 'number') params.limit = limit;
  const res = await apiClient.get(
    `${OBT}/decision-sets/${enc(decisionSetId)}/director-park-candidates`,
    { params, signal }
  );
  return res.data; // DirectorParkCandidatesOut {decision_set_id, limit, returned, items}
}
export async function addDirectorPark(decisionSetId, { parkId, reason } = {}, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/decision-sets/${enc(decisionSetId)}/director-parks`,
    { park_id: parkId, reason },
    { signal }
  );
  return res.data; // DecisionSetResult
}
export async function removeDirectorPark(decisionSetId, itemId, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/decision-sets/${enc(decisionSetId)}/director-parks/${enc(itemId)}/remove`,
    {},
    { signal }
  );
  return res.data; // DecisionSetResult
}
export async function getDecisionSetHistory(campaignId, { signal } = {}) {
  const res = await apiClient.get(`${OBT}/campaigns/${enc(campaignId)}/decision-sets`, {
    signal,
  });
  return res.data; // DecisionSetHistoryOut {campaign_id, versions: [...]}
}

// ---------------------------------------------------------------------------
// Kampanya bağlamı + cevap-durumu (rapor uçları, salt-okunur)
// ---------------------------------------------------------------------------

export async function listCampaigns({ topic, status, limit, offset } = {}, { signal } = {}) {
  const params = {};
  if (topic) params.topic = topic;
  if (status) params.status = status;
  if (typeof limit === 'number') params.limit = limit;
  if (typeof offset === 'number') params.offset = offset;
  const res = await apiClient.get(`${OBT}/campaigns`, { params, signal });
  return res.data; // {page, items}
}
export async function getCampaign(campaignId, { signal } = {}) {
  const res = await apiClient.get(`${OBT}/campaigns/${enc(campaignId)}`, { signal });
  return res.data; // CampaignHistoryOut (description dahil)
}
export async function listPendingRespondents(campaignId, { limit, offset } = {}, { signal } = {}) {
  const params = {};
  if (typeof limit === 'number') params.limit = limit;
  if (typeof offset === 'number') params.offset = offset;
  const res = await apiClient.get(`${OBT}/campaigns/${enc(campaignId)}/pending`, {
    params,
    signal,
  });
  return res.data; // RespondentStatusPage
}
export async function listLateRespondents(campaignId, { limit, offset } = {}, { signal } = {}) {
  const params = {};
  if (typeof limit === 'number') params.limit = limit;
  if (typeof offset === 'number') params.offset = offset;
  const res = await apiClient.get(`${OBT}/campaigns/${enc(campaignId)}/late`, {
    params,
    signal,
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Personel adı çözümü — respondent_personnel_id → ad (alt zincirden, kapsamlı)
// ---------------------------------------------------------------------------

export async function fetchChildChain(personnelId, { signal } = {}) {
  const res = await apiClient.get(`${ORG}/chains/child/${enc(personnelId)}`, {
    params: { include_self: false },
    signal,
  });
  return Array.isArray(res.data?.chain) ? res.data.chain : [];
}
